import React, {
  memo,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useI18n } from "Hooks";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classNames from "classnames";
import _ from "lodash";
import withDerivedProps from "lib/withDerivedProps";

// design system
import {
  Modal,
  DSProvider,
  Button,
  IconButton,
  Dropdown,
  FilterLabelButton,
  DropdownMenu,
} from "@toddle-edu/ds-web";
import { ChevronLeftOutlined } from "@toddle-edu/ds-icons";

// components
import FullScreenLoader from "UIComponents/FullScreenLoader";
import StandardsSetTable from "./components/StandardsSetTable";
import StandardsTable from "./components/StandardsTable";
import Breadcrumbs from "./components/Breadcrumbs";

// utils
import { getIsToolPresent } from "Utils";
import {
  getDataSetScopeDropdownOptions,
  getInitialSelectedStandardsForSet,
  getPublishedSubjectStandardQueryVariables,
  getStandardEdges,
} from "./modules/Utils";
import ACLStore from "lib/aclStore";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";

// constants
import { CREATE_STEPS, DATA_SCOPE_ENUM, EDIT_STEPS } from "./modules/Constants";
import {
  GRADE_SCALE_TOOL,
  CURRICULUM_TYPE_PYP,
} from "Constants/stringConstants";
import { STANDARD_UIDS } from "modules/Services/ServicesConstants";

// modules
import {
  getOrganizationId,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import {
  getLearningCourseStandardsFromCache,
  getPublishedSubjectStandardDetailsFromCache,
} from "./modules/StandardsSelectionModalGraphqlHelpers";
import { syncStandardsWithStandardBasedRubric } from "modules/AssessmentTool/AssessmentToolModule";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";

// scss
import classes from "./StandardsSelectionModal.scss";

const EMPTY_ARRAY = [];

const styles = {
  modalBody: {
    overflow: "hidden",
    padding: "20px",
  },
  standardSelectionModalBody: {
    overflow: "hidden",
    padding: "0px",
  },
  modalHeader: {
    padding: "12px 24px",
    marginBottom: 0,
  },
  modalFooter: {
    padding: "12px 24px",
  },
};

const StandardsSelectionModal = memo(props => {
  const {
    steps,
    mode,
    onClose,
    onSelectClick,
    learningCourseId,
    initialSelectedStandards = {},
    initialStandardSet,
    curriculumProgramType,
    assessmentSubjects,
    assessmentId,
    syncStandardsWithStandardBasedRubric,
    shouldSyncStandards = true,
    standardNodes,
    assessmentTools,
    isGradeScaleToolPresent,
    canMapGradeScale = false,
    allowMultiSetSelection = false,
    isUnitPlanTagged = false,
    unitName,
    unitTaggedStandardSetIds,
    unitTaggedStandardIds,
  } = props;

  const isLearningGoalsRevampEnabled = ACLStore.can(
    "FeatureFlag:LearningGoalsRevamp"
  );

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  // Compute whether UNIT scope should be disabled
  const isUnitScopeDisabled =
    isUnitPlanTagged &&
    _.isEmpty(unitTaggedStandardSetIds) &&
    _.isEmpty(unitTaggedStandardIds);

  const getInitialDataScope = () => {
    if (
      isLearningGoalsRevampEnabled &&
      isUnitPlanTagged &&
      !isUnitScopeDisabled
    ) {
      return DATA_SCOPE_ENUM.UNIT;
    }
    if (isLearningCoursesEnabled && !_.isEmpty(learningCourseId)) {
      return DATA_SCOPE_ENUM.LEARNING_COURSE;
    }
    return DATA_SCOPE_ENUM.ALL;
  };

  // states
  const [currentStep, setCurrentStep] = useState(_.first(steps).id);

  const [
    selectedStandardSetDataScope,
    setSelectedStandardSetDataScope,
  ] = useState(getInitialDataScope);

  const [selectedStandardsDataScope, setSelectedStandardsDataScope] = useState(
    getInitialDataScope
  );

  const [selectedStandardSet, setSelectedStandardSet] = useState(
    () => initialStandardSet || {}
  );
  const [selectedGradeScale, setSelectedGradeScale] = useState([]); // this is the actual selected grade scale (from the table)

  const computedInitialSelectedStandards = useMemo(() => {
    if (!_.isEmpty(initialSelectedStandards)) {
      // Callers must pass object shape { [setId]: string[] }.
      return initialSelectedStandards;
    }

    if (!_.isEmpty(selectedStandardSet) && !_.isEmpty(standardNodes)) {
      const seeded = getInitialSelectedStandardsForSet({
        nodes: standardNodes,
        setId: selectedStandardSet.id,
      });
      return _.isEmpty(seeded) ? {} : { [selectedStandardSet.id]: seeded };
    }

    return {};
  }, [initialSelectedStandards, selectedStandardSet, standardNodes]);

  const [selectedStandards, setSelectedStandards] = useState(
    () => computedInitialSelectedStandards
  );

  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  // refs
  const isInitialValueSet = useRef(false);

  // i18n
  const { t } = useI18n();

  // side effects

  // Update selected standards when computed initial standards change.
  // Works for both array (legacy) and object (new) shapes via _.isEmpty.
  useEffect(() => {
    if (
      !_.isEmpty(computedInitialSelectedStandards) &&
      !isInitialValueSet.current
    ) {
      setSelectedStandards(computedInitialSelectedStandards);
      isInitialValueSet.current = true;
    }
  }, [computedInitialSelectedStandards]);

  const onChangeStandardSet = useCallback(
    params => {
      const {
        standardSet,
        gradeScale,
        shouldResetSelectedStandards = false,
      } = params;

      setSelectedStandardSet(standardSet);
      setSelectedGradeScale(gradeScale);

      if (shouldResetSelectedStandards && !allowMultiSetSelection) {
        setSelectedStandards({});
      }

      const currentStepIndex = _.findIndex(
        steps,
        step => step.id === currentStep
      );

      const nextStepIndex = currentStepIndex + 1;

      if (nextStepIndex < steps.length) {
        setCurrentStep(steps[nextStepIndex].id);
      }
    },
    [currentStep, steps, allowMultiSetSelection]
  );

  const onGoToPreviousStep = useCallback(() => {
    if (canMapGradeScale || !allowMultiSetSelection) {
      setSelectedStandards({}); // reset selected standards when user goes back to the previous step
    }

    const currentStepIndex = _.findIndex(
      steps,
      step => step.id === currentStep
    );

    const previousStepIndex = currentStepIndex - 1;

    if (previousStepIndex >= 0) {
      setCurrentStep(steps[previousStepIndex].id);
    }
  }, [currentStep, steps, canMapGradeScale, allowMultiSetSelection]);

  const handleStandardSetScopeChange = useCallback(({ key: selectedScope }) => {
    setSelectedStandardSetDataScope(selectedScope);
  }, []);

  const handleStandardsScopeChange = useCallback(({ key: selectedScope }) => {
    setSelectedStandardsDataScope(selectedScope);
  }, []);

  const dataScopeDropdownOptions = useMemo(() => {
    return getDataSetScopeDropdownOptions({
      t,
      isUnitPlanTagged,
      unitName,
      isUnitScopeDisabled,
    });
  }, [t, isUnitPlanTagged, unitName, isUnitScopeDisabled]);

  const selectedStandardSetDataScopeOption = useMemo(() => {
    return _.find(dataScopeDropdownOptions, {
      value: selectedStandardSetDataScope,
    });
  }, [dataScopeDropdownOptions, selectedStandardSetDataScope]);

  const selectedStandardsDataScopeOption = useMemo(() => {
    return _.find(dataScopeDropdownOptions, {
      value: selectedStandardsDataScope,
    });
  }, [dataScopeDropdownOptions, selectedStandardsDataScope]);

  const renderDataScopeDropdown = useCallback(
    ({ selectedValue, onChange, label, labelValue }) => {
      if (!isLearningGoalsRevampEnabled || _.isEmpty(learningCourseId))
        return null;

      return (
        <Dropdown
          dsVersion="2.0"
          overlay={
            <DropdownMenu
              dsVersion="2.0"
              options={dataScopeDropdownOptions}
              value={selectedValue}
              onClick={onChange}
              showSelection={true}
            />
          }
        >
          <FilterLabelButton
            dsVersion="2.0"
            label={label}
            labelValue={labelValue}
            variant="neutral"
            type="outlined"
          />
        </Dropdown>
      );
    },
    [dataScopeDropdownOptions, learningCourseId, isLearningGoalsRevampEnabled]
  );

  const renderHeader = useCallback(() => {
    switch (currentStep) {
      case "STANDARD_SET_SELECTION": {
        return (
          <header
            className={classNames(
              classes.contentSpaceBetween,
              classes.gap,
              classes.w100
            )}
          >
            <span>
              {t("common:select_with_label", {
                label: t("scopeAndSequence:standard_set"),
              })}
            </span>

            {renderDataScopeDropdown({
              label: t("classRoom:show_standard_sets"),
              labelValue: selectedStandardSetDataScopeOption?.label,
              onChange: handleStandardSetScopeChange,
              selectedValue: selectedStandardSetDataScope,
            })}
          </header>
        );
      }
      case "STANDARDS_SELECTION": {
        return (
          <header
            className={classNames(
              classes.contentSpaceBetween,
              classes.gap,
              classes.w100
            )}
          >
            <div className={classNames(classes.gap)}>
              {steps.length > 1 ? (
                <IconButton
                  icon={<ChevronLeftOutlined />}
                  size={"medium"}
                  variant="neutral"
                  type="plain"
                  onClick={onGoToPreviousStep}
                />
              ) : null}

              <div className={classes.breadCrumbsContainer}>
                <h6 style={{ display: "inline" }}>
                  {selectedStandardSet?.title}
                </h6>

                {_.includes(_.map(steps, "id"), "STANDARD_SET_SELECTION") ? (
                  <Breadcrumbs
                    selectedStandardSetDataScope={selectedStandardSetDataScope}
                    learningCourseId={learningCourseId}
                    assessmentSubjects={assessmentSubjects}
                    onGoToPreviousStep={onGoToPreviousStep}
                    selectedStandardSet={selectedStandardSet}
                    selectedGradeScale={selectedGradeScale}
                    onChangeStandardSet={onChangeStandardSet}
                    hasSelectedStandards={!_.isEmpty(selectedStandards)}
                    assessmentId={assessmentId}
                    curriculumProgramType={curriculumProgramType}
                    canMapGradeScale={canMapGradeScale}
                  />
                ) : null}
              </div>
            </div>

            {renderDataScopeDropdown({
              label: t("classRoom:show_standards"),
              labelValue: selectedStandardsDataScopeOption?.label,
              onChange: handleStandardsScopeChange,
              selectedValue: selectedStandardsDataScope,
            })}
          </header>
        );
      }
    }
  }, [
    currentStep,
    t,
    renderDataScopeDropdown,
    selectedStandardSetDataScopeOption?.label,
    selectedStandardSetDataScope,
    steps,
    onGoToPreviousStep,
    selectedStandardSet,
    learningCourseId,
    assessmentSubjects,
    selectedGradeScale,
    onChangeStandardSet,
    selectedStandards,
    assessmentId,
    curriculumProgramType,
    canMapGradeScale,
    selectedStandardsDataScopeOption?.label,
    selectedStandardsDataScope,
    handleStandardSetScopeChange,
    handleStandardsScopeChange,
  ]);

  const setActiveSelection = useCallback(
    nextFlatArray => {
      const activeSetId = selectedStandardSet?.id;

      setSelectedStandards(prev => {
        if (_.isEmpty(nextFlatArray)) {
          // Keep empty state canonical: {} (no key for empty set)
          return _.omit(prev, activeSetId);
        }
        return { ...prev, [activeSetId]: nextFlatArray };
      });
    },
    [selectedStandardSet?.id]
  );

  const renderBody = () => {
    switch (currentStep) {
      case "STANDARD_SET_SELECTION": {
        return (
          <StandardsSetTable
            onSelectStandardSet={onChangeStandardSet}
            selectedStandardSetDataScope={selectedStandardSetDataScope}
            learningCourseId={learningCourseId}
            assessmentSubjects={assessmentSubjects}
            assessmentId={assessmentId}
            assessmentTools={assessmentTools}
            isGradeScaleToolPresent={isGradeScaleToolPresent}
            canMapGradeScale={canMapGradeScale}
            unitTaggedStandardSetIds={unitTaggedStandardSetIds}
          />
        );
      }
      case "STANDARDS_SELECTION": {
        const activeSetId = selectedStandardSet?.id;
        const activeSelection = selectedStandards[activeSetId] ?? EMPTY_ARRAY;
        const initialActiveSlice =
          computedInitialSelectedStandards[activeSetId] ?? EMPTY_ARRAY;

        return (
          <StandardsTable
            selectedStandardSet={selectedStandardSet}
            selectedStandards={activeSelection}
            setSelectedStandards={setActiveSelection}
            learningCourseId={learningCourseId}
            selectedStandardsDataScope={selectedStandardsDataScope}
            onChangeStandardsDataScope={handleStandardsScopeChange}
            initialSelectedStandards={initialActiveSlice}
            unitTaggedStandardIds={unitTaggedStandardIds}
          />
        );
      }
      default:
        return null;
    }
  };

  const onClickSelect = async () => {
    setShowFullScreenLoader(true);

    // Returns the cached standard edges for a given standardSetId.
    // Handles both showCourseStandards and direct-set paths.
    // Falls back to [] if the cache entry is missing (e.g. eviction).
    const getEdgesForSet = setId => {
      let node;
      if (
        selectedStandardsDataScope === DATA_SCOPE_ENUM.LEARNING_COURSE &&
        learningCourseId
      ) {
        const variables = getPublishedSubjectStandardQueryVariables({
          id: learningCourseId,
          standardSetIds: _.compact([setId]),
          curriculumProgramType,
        });
        node = getLearningCourseStandardsFromCache(variables);
      } else {
        const variables = getPublishedSubjectStandardQueryVariables({
          id: setId,
          curriculumProgramType,
        });
        node = getPublishedSubjectStandardDetailsFromCache(variables);
      }
      const setEdges = getStandardEdges({ node, selectedStandardsDataScope });
      if (_.isEmpty(setEdges) && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(
          `[StandardsSelectionModal] No cached edges found for standardSetId=${setId}. Falling back to empty list.`
        );
      }
      return setEdges ?? [];
    };

    // Prepare flat arrays for rubric sync (unchanged).
    const flatSelectedIds = Object.values(selectedStandards).flat();
    const initialFlatIds = Object.values(
      computedInitialSelectedStandards
    ).flat();

    // Build per-set sorted output objects using each set's own cache entry.
    const perSetSorted = {};
    const perSetNodes = {};
    const perSetElement = {};

    for (const [setId, ids] of Object.entries(selectedStandards)) {
      const setEdges = getEdgesForSet(setId);
      const edgeIndex = _.fromPairs(
        _.map(setEdges, ({ node }, i) => [node?.id, i])
      );
      perSetSorted[setId] = _.sortBy(ids, id => _.get(edgeIndex, id, Infinity));
      perSetNodes[setId] = _.map(setEdges, "node");
      perSetElement[setId] = _.get(
        setEdges,
        "0.node.standardsWrapper.standardSet.type"
      );
    }

    await onSelectClick({
      selectedStandards: perSetSorted,
      selectedGradeScale,
      elementType: perSetElement,
      nodes: perSetNodes,
    });

    if (shouldSyncStandards && assessmentId) {
      if (allowMultiSetSelection) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn(
            "[StandardsSelectionModal] shouldSyncStandards is not supported when allowMultiSetSelection is true; skipping rubric sync."
          );
        }
      } else {
        await syncStandardsWithStandardBasedRubric({
          assessmentId,
          selectedStandards: flatSelectedIds,
          initialSelectedStandards: initialFlatIds,
        });
      }
    }

    setShowFullScreenLoader(false);
  };

  const totalSelectedStandardsCount = _.sumBy(
    Object.values(selectedStandards),
    "length"
  );
  const areStandardsSelected = totalSelectedStandardsCount > 0;
  const isCurrentStepStandardSelection = _.isEqual(
    currentStep,
    "STANDARDS_SELECTION"
  );

  const showFooter = isCurrentStepStandardSelection || !canMapGradeScale;

  const standardsCountClass = classNames("text-label", {
    [classes.textSecondary]: areStandardsSelected,
  });

  return (
    <DSProvider dsVersion="2.0">
      <Modal height="80vh" width="80vw" onClose={onClose}>
        <Modal.Header isClosable hasSeparator style={styles.modalHeader}>
          {renderHeader()}
        </Modal.Header>
        <Modal.Body
          isScrollable={false}
          style={
            isCurrentStepStandardSelection
              ? styles.standardSelectionModalBody
              : styles.modalBody
          }
        >
          <div className={classes.bodyContainer}>{renderBody()}</div>
        </Modal.Body>

        {showFooter ? (
          <Modal.Footer hasSeparator style={styles.modalFooter}>
            <footer className={classes.contentSpaceBetween}>
              <span className={standardsCountClass}>
                {areStandardsSelected
                  ? t("common:standards_selected_with_count", {
                      count: totalSelectedStandardsCount,
                    })
                  : null}
              </span>

              <div className={classes.footerRight}>
                <Button
                  size={"medium"}
                  onClick={onClose}
                  variant="neutral"
                  type={"outlined"}
                >
                  {t("common:cancel")}
                </Button>

                <Button
                  size={"medium"}
                  onClick={onClickSelect}
                  variant={"progressive"}
                  type={"fill"}
                  disabled={_.isEmpty(selectedStandards)}
                >
                  {mode === "edit" ? t("common:update") : t("common:select")}
                </Button>
              </div>
            </footer>
          </Modal.Footer>
        ) : null}

        {showFullScreenLoader ? (
          <FullScreenLoader overlayContainer={classes.overlayContainer} />
        ) : null}
      </Modal>
    </DSProvider>
  );
});

StandardsSelectionModal.displayName = "StandardsSelectionModal";

StandardsSelectionModal.defaultProps = {
  mode: "create",
};

const mapStateToProps = state => {
  const orgId = getOrganizationId();
  const academicYearId = getSelectedAcademicYearId(state);

  const { id: curriculumProgramId, type: curriculumProgramType } =
    _.get(state, "platform.currentCurriculumProgram", {}) || {};

  return {
    orgId,
    curriculumProgramId,
    curriculumProgramType,
    academicYearId,
  };
};

const mapActionCreators = {
  syncStandardsWithStandardBasedRubric,
};

const getDerivedProps = props => {
  const {
    assessmentId,
    mode = "create",
    curriculumProgramType,
    academicYearId,
  } = props;

  const isPYPCurriculumProgram = _.isEqual(
    curriculumProgramType,
    CURRICULUM_TYPE_PYP
  );

  const steps = mode === "create" ? CREATE_STEPS : EDIT_STEPS;

  const standardUID = STANDARD_UIDS[curriculumProgramType];

  const assessmentDetails = getAssessmentDetailsFromCache({
    id: assessmentId,
    collaboratorsFilter: {
      userAcademicYearId: academicYearId,
    },
  });

  const { allFields: assessmentFields, assessmentTools } =
    assessmentDetails || {};

  const subjects = _.find(assessmentFields, { uid: "subjects" })?.value;
  const standardField = _.find(assessmentFields, { uid: standardUID });

  const resolvedTree = standardField?.resolvedMinimalTree;
  const nodes = isPYPCurriculumProgram
    ? _.get(resolvedTree, "benchmarks", [])
    : _.get(resolvedTree, "nodes", []);

  const standardNodes = _(nodes)
    .filter(
      ({ gradingType, id } = {}) =>
        gradingType === "INDIVIDUAL" && _.includes(standardField?.value, id)
    )
    .value();

  // Extract unit plan data for UNIT scope dropdown
  const unitPlanField = _.find(assessmentFields, { uid: "unitPlan" });

  const unitPlan = _.get(unitPlanField, "resolvedMinimalTree.unitPlan", {});
  const unitName = _.get(unitPlan, "title.value", "");
  const isUnitPlanTagged = !_.isEmpty(unitPlan);

  // Derive standard set IDs tagged in the unit plan
  const unitPlanFields = _.get(unitPlan, "allFields", []);
  const unitStandardField = _.find(unitPlanFields, { uid: standardUID });
  const unitResolvedTree = _.get(unitStandardField, "resolvedMinimalTree", {});
  const unitTaggedStandardsNodes = isPYPCurriculumProgram
    ? _.get(unitResolvedTree, "benchmarks", [])
    : _.get(unitResolvedTree, "nodes", []);
  const unitTaggedStandardSetIds = _(unitTaggedStandardsNodes)
    .map("standardsWrapper.standardSet.id")
    .compact()
    .uniq()
    .value();

  const unitTaggedStandardIds = _(unitTaggedStandardsNodes)
    .map("id")
    .compact()
    .uniq()
    .value();

  const isGradeScaleToolPresent = getIsToolPresent(
    assessmentTools,
    GRADE_SCALE_TOOL
  );

  return {
    steps,
    standardNodes,
    assessmentSubjects: subjects,
    isUnitPlanTagged,
    unitName,
    unitTaggedStandardSetIds,
    unitTaggedStandardIds,
    isGradeScaleToolPresent,
    assessmentTools,
    assessmentFields,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps)
)(StandardsSelectionModal);
